import ReactSelect, { components } from 'react-select';
import { TextSpan } from '../../../app/typography';
import {
  displayBreakpoints,
  isMobile,
  useWindowSize,
} from '../../../app/spacing';
import { dropdownColors } from '../../../app/colors';
import {
  StyledLabel,
  StyledLabelWrapper,
  reactSelectStyles,
  StyledCustomOption,
} from './styles';
import Icon from '../Icon';
import { useTranslation } from 'react-i18next';
import { gray } from '@kizen/kds/Colors';

const getValue = (value, options) => {
  // if the value is a string, find that in the options and return it
  if (typeof value === 'string') {
    return options.find((option) => option.value === value) || null;
  }

  // if the value is an object, key off of the id
  if (value && 'id' in value) {
    return options.find((option) => option.value.id === value.id);
  }

  return value;
};

export const SingleValue = ({ children, ...propsSingleValue }) => {
  const Text = propsSingleValue.selectProps.TextComponent || TextSpan;
  return (
    <components.SingleValue {...propsSingleValue}>
      <Text
        value={propsSingleValue.selectProps.value}
        size={propsSingleValue.selectProps.textSize}
        style={{
          textIndent: '2px',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          margin: 0,
        }}
      >
        {children}
      </Text>
    </components.SingleValue>
  );
};

const Select = ({
  className,
  label,
  textSize,
  size,
  style,
  styles,
  disabled,
  error,
  options,
  value,
  fullWidth,
  dropUp = false,
  components: componentsProp,
  onKeyDown,
  dropdownIcon,
  placeholder,
  ...props
}) => {
  const { width } = useWindowSize();
  const { t } = useTranslation();
  const NoOptionsMessage = ({ children, ...propsNoOption }) => {
    return (
      <components.NoOptionsMessage {...propsNoOption}>
        <TextSpan size={textSize} style={{ color: gray['gray-600'] }}>
          {t('No Options')}
        </TextSpan>
      </components.NoOptionsMessage>
    );
  };

  const DropdownIndicator = () => (
    <Icon
      icon={dropdownIcon}
      size="2x"
      style={{
        textAlign: 'left',
        padding: '0 8px !important',
      }}
      color={gray['gray-600']}
    />
  );

  if (label) {
    return (
      <div className={className}>
        <StyledLabelWrapper>
          <StyledLabel disabled={disabled}>{label}</StyledLabel>
          <ReactSelect
            styles={{ ...reactSelectStyles, ...styles }}
            components={{
              SingleValue,
              NoOptionsMessage,
              DropdownIndicator,
              Option: StyledCustomOption,
              ...componentsProp,
            }}
            placeholder={
              //  When used Placholder component in components array click on placeholder is not triggering opening menu
              <TextSpan size={textSize} color={gray['gray-600']}>
                {placeholder}
              </TextSpan>
            }
            options={options}
            // TODO for some reason react-select anticipates a value of the shape { value, label },
            // i.e. one of the values in options.
            value={getValue(value, options)}
            mobile={isMobile(width, displayBreakpoints.desktop)}
            fullWidth={fullWidth}
            onKeyDown={(ev) => {
              if (onKeyDown) {
                onKeyDown(ev, { options });
              }
            }}
            textSize={textSize}
            {...props}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary25: dropdownColors.hover,
              },
              controlHeight: 36,
            })}
            isDisabled={disabled}
            error={error}
            dropUp={dropUp}
          />
        </StyledLabelWrapper>
      </div>
    );
  }

  return (
    <div className={className}>
      <ReactSelect
        styles={{ ...reactSelectStyles, ...styles }}
        components={{
          SingleValue,
          NoOptionsMessage,
          DropdownIndicator,
          Option: StyledCustomOption,
          ...componentsProp,
        }}
        placeholder={
          //  When used Placholder component in components array click on placeholder is not triggering opening menu
          <TextSpan size={textSize} color={gray['gray-600']}>
            {placeholder}
          </TextSpan>
        }
        options={options}
        // TODO for some reason react-select anticipates a value of the shape { value, label },
        // i.e. one of the values in options.
        value={getValue(value, options)}
        mobile={isMobile(width, displayBreakpoints.desktop)}
        fullWidth={fullWidth}
        onKeyDown={(ev) => {
          if (onKeyDown) {
            onKeyDown(ev, { options });
          }
        }}
        textSize={textSize}
        {...props}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary25: dropdownColors.hover,
          },
          controlHeight: 36,
        })}
        isDisabled={disabled}
        error={error}
      />
    </div>
  );
};

Select.defaultProps = {
  value: null,
  label: '',
  onChange: () => {},
  options: null,
  className: '',
  textSize: 'text',
  placeholder: '',
  styles: {},
  size: 'small',
  disabled: false,
  error: false,
  fullWidth: false,
  inlineMenu: false,
  components: null,
  onKeyDown: null,
  dropdownIcon: 'down',
};

export default Select;
